// /app/frontend/src/components/EligibilityGateModal.jsx
import React, { useContext } from "react";
import { X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { buildVerifyUrl, goVerify } from "../utils/verificationNav";
import { AuthContext } from "../App";

const DEFAULTS = {
  verification_required: {
    title: "Verification required",
    body: "Speed Dating is only available to verified users to keep the room safe and real.",
    cta: "Verify now",
    href: "/verify-age", // Will be enhanced with return URL
    isVerifyRoute: true,
  },
  identity_not_verified: {
    title: "Verification required",
    body: "Speed Dating is only available to verified users to keep the room safe and real.",
    cta: "Verify now",
    href: "/verify-age",
    isVerifyRoute: true,
  },
  elite_required: {
    title: "Elite required",
    body: "This event is reserved for Elite members.",
    cta: "View plans",
    href: "/shop",
  },
  lounge_pass_required: {
    title: "Pass required",
    body: "You'll need a Lounge Pass to join this event.",
    cta: "Get Lounge Pass",
    href: "/shop",
  },
  inner_circle_required: {
    title: "Inner Circle required",
    body: "This event is exclusive to Inner Circle members.",
    cta: "Join Inner Circle",
    href: "/shop?plan=inner-circle",
  },
  profile_incomplete: {
    title: "Finish your profile",
    body: "Complete your profile so matches can identify you during the event.",
    cta: "Complete profile",
    href: "/edit-profile",
  },
  age_verification_required: {
    title: "Age verification required",
    body: "Please verify your age to participate in Speed Dating events.",
    cta: "Verify age",
    href: "/verify-age",
    isVerifyRoute: true,
  },
  video_budget_exceeded: {
    title: "Minutes limit reached",
    body: "You've used your speed dating minutes for this month. Minutes reset monthly. Upgrade for more time!",
    cta: "View plans",
    href: "/shop",
  },
  lounge_minutes_exceeded: {
    title: "Lounge time limit reached",
    body: "You've used your daily lounge minutes. Minutes reset every day. Get a Lounge Pass for more time!",
    cta: "Get Lounge Pass",
    href: "/shop",
  },
  reauth_required: {
    title: "Confirm it's you",
    body: "For security, please re-confirm your password to join the event.",
    cta: "Go to settings",
    href: "/settings",
  },
  not_authenticated: {
    title: "Sign in required",
    body: "Please sign in to join the event.",
    cta: "Sign in",
    href: "/auth",
  },
  insufficient_coins: {
    title: "Not enough LoveCoins",
    body: "You don't have enough LoveCoins to join this event.",
    cta: "Get LoveCoins",
    href: "/shop",
  },
  event_full: {
    title: "Event is full",
    body: "This event has reached its maximum capacity. Check back for future events.",
    cta: "View other events",
    href: "/events/speed-dating",
  },
  event_not_started: {
    title: "Event not started",
    body: "This event hasn't started yet. Please wait for the scheduled time.",
    cta: "Got it",
    href: null,
  },
  event_ended: {
    title: "Event ended",
    body: "This event has already ended. Check out upcoming events.",
    cta: "View upcoming",
    href: "/events/speed-dating",
  },
  ticket_required: {
    title: "Ticket required",
    body: "This is a ticketed event. Purchase a ticket to join.",
    cta: "Buy Ticket",
    href: null, // Will be handled specially to trigger checkout
  },
  event_minutes_exhausted: {
    title: "Event time used",
    body: "You've used your 60 minutes of participation for this event. Each ticket includes up to 60 minutes of live participation.",
    cta: "Explore Events",
    href: "/events/speed-dating",
  },
  unknown: {
    title: "Unable to join right now",
    body: "Something is preventing entry. Try again or check your account status.",
    cta: "Go to settings",
    href: "/settings",
  },
};

export default function EligibilityGateModal({
  open,
  reason,
  message,
  onClose,
  onAction,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  if (!open) return null;

  const preset = DEFAULTS[reason] || DEFAULTS.unknown;
  const title = preset.title;
  const body = message || preset.body;
  const cta = preset.cta;
  
  // Build the action URL - add return param for verify routes
  let href = preset.href;
  if (preset.isVerifyRoute && href) {
    // Use canonical verification URL builder with return to current page
    const returnTo = `${location.pathname}${location.search}`;
    href = buildVerifyUrl(returnTo);
  }

  const handleAction = () => {
    // For verify routes, use goVerify with analytics
    if (preset.isVerifyRoute) {
      const returnTo = `${location.pathname}${location.search}`;
      goVerify(navigate, location, returnTo, { source: 'eligibility_gate', isLoggedIn: !!user });
      onClose();
      return;
    }
    
    if (href) {
      onAction(href);
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div className="relative w-[92%] max-w-md rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon based on reason */}
        <div className="mb-3">
          {reason === "verification_required" || reason === "identity_not_verified" ? (
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <span className="text-2xl">🛡️</span>
            </div>
          ) : reason === "elite_required" || reason === "inner_circle_required" ? (
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
              <span className="text-2xl">👑</span>
            </div>
          ) : reason === "not_authenticated" ? (
            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
              <span className="text-2xl">🔐</span>
            </div>
          ) : reason === "age_verification_required" ? (
            <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
              <span className="text-2xl">🎂</span>
            </div>
          ) : reason === "insufficient_coins" ? (
            <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <span className="text-2xl">💰</span>
            </div>
          ) : reason === "video_budget_exceeded" ? (
            <div className="w-12 h-12 rounded-full bg-pink-500/20 flex items-center justify-center">
              <span className="text-2xl">⏱️</span>
            </div>
          ) : reason === "lounge_minutes_exceeded" ? (
            <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
              <span className="text-2xl">🛋️</span>
            </div>
          ) : (
            <div className="w-12 h-12 rounded-full bg-rose-500/20 flex items-center justify-center">
              <span className="text-2xl">⚠️</span>
            </div>
          )}
        </div>

        <div className="text-lg font-semibold text-white">{title}</div>
        <div className="mt-2 text-sm text-gray-300 leading-relaxed">{body}</div>

        <div className="mt-5 flex gap-2">
          <button
            className="flex-1 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-medium text-white hover:bg-white/15 transition"
            onClick={onClose}
          >
            Not now
          </button>
          <button
            className="flex-1 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 px-4 py-2.5 text-sm font-semibold text-white hover:from-pink-400 hover:to-rose-400 transition shadow-lg"
            onClick={handleAction}
          >
            {cta}
          </button>
        </div>
      </div>
    </div>
  );
}
